import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  BaseButton,
  BaseDialog,
  BaseDivider,
  BaseInputLabel,
  FormikField,
  BaseTextField,
  BaseTypography
} from 'src/components/common';
import { EmailTemplatesSelect } from 'src/components/fields/selects/EmailTemplatesSelect/EmailTemplatesSelect';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import { validationErrors } from 'src/utils/validation/validationErrors';
import { validationRules } from 'src/utils/validation/validationRules';
import { IBaseForm } from 'src/utils/types/forms';
import { FormFieldsContainer } from 'src/components/common/FormFieldsGroup/FormFieldsGroup';
import { useSelector } from 'react-redux';
import {
  getDefaultEmailTemplate,
  getEmailTemplatesForSelect,
} from 'src/store/lists/emailTemplates/helpers';
import ReactPlayer from 'react-player';
import { Link, FormControl, Avatar } from '@material-ui/core';
import { RootState } from 'src/store';
import { IGuestInfo } from 'src/api/base/Guest/types';
import SunEditor, { buttonList } from 'suneditor-react';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { makeStyles } from '@material-ui/core/styles';
import { BaseButtonGroup } from '../../../../components/common/BaseButton/BaseButtonGroup';
import noUserPicture from 'src/assets/images/no-user-picture.svg';
import { BaseChip } from 'src/components/common/BaseChip/BaseChip';

export type PContactGuestModal = IBaseForm<any> & {
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    marginBottom: '1rem',
    maxWidth: '100%',
  },
  videoWrapper: {
    width: '100%',
    height: '100%',
    margin: '10px 0',
  },
  companyDescriptionWrapper: {
    border: '0.5px solid gainsboro',
    padding: '10px',
    borderRadius: '15px',
    whiteSpace: 'pre-wrap',
  },
  avatar: {
    width: 200,
    height: 200,
  },
  video: {
    marginBottom: 33,
  },
  avatarWidth: {
    width: 77,
    height: 77,
  },
  userData: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
	gap: '10px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  userImgAndPicture: {
    display: 'flex',
  },
  userNameSection: {
    marginLeft: '10px',
  },
  userName: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '140%',
    color: theme.palette.primary.contrastText,
  },
  field: {
    [theme.breakpoints.down('sm')]: {
      marginTop: '5px',
    },
  },
  field__label: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '140%',
    letterSpacing: '0.03em',
    color: '#949C9E',
  },
  field__value: {
    fontWeight: 500,
    fontSize: '14px',
  },
  field__value_country: {
    fontWeight: 500,
    fontSize: '14px',
	display: 'inline-block',
  },
  field__value_language: {
	fontWeight: 500,
    fontSize: '14px',
  },
  expertises: {
    margin: '0px 0 0 -10px',
  },
  expertises__item: {
    margin: '2px 0 5px 10px',
	display: 'inline-block',
  },
  userLabel: {
	  color: '#ababab'
  },
  hr: {
	  width: 'calc(100% + 40px)',
      position: 'relative',
      marginLeft: '-20px',
	  marginTop: '20px',
      marginBottom: '0',
	  background: '#78c5f182',
	  height: '1px',
	  border: '0',
  }
}));

const Video: React.FC<{ url: string }> = (props) => {
  const { url } = props;
  
  const [canPlay, setCanPlay] = useState<boolean>(ReactPlayer.canPlay(url));

  const styles = useStyles();

  if (url.includes('vimeo.com')) {
    return (
      <>
        <Link href={url} target="_blank" rel="noreferrer">
          {url}
        </Link>
        <br />
      </>
    );
  }

  return canPlay ? (
    <ReactPlayer
      onError={(_) => setCanPlay(false)}
      url={url}
      width="100%"
      height="400px"
      className={styles.video}
    />
  ) : (
    <>
      <Link href={url} target="_blank" rel="noreferrer">
        {url}
      </Link>
      <br />
    </>
  );
};

export const GuestVideoList: React.FC<{ guest: IGuestInfo | null }> = (
  props,
) => {
  const { guest } = props;

  const styles = useStyles();

  const videos = guest?.videos?.map((url) => (
    <>
      <Video url={url} /> <br />
    </>
  ));

  const available = guest?.available?.map((e) => e.name).join(', ');
  const companyLogo = guest?.guest_company_logo || guest?.manager_company_logo;
  const companyDescription =
    guest?.guest_company_description || guest?.manager_company_description;

  return (
    <div className={styles.contentWrapper}>
	  <div className={styles.userData}>
        <div className={styles.userImgAndPicture}>
          <Avatar
            variant="square"
            className={styles.avatarWidth}
            src={guest?.avatar || noUserPicture}
            alt={guest?.first_name}
          />
          <div className={styles.userNameSection}>
            <BaseTypography className={styles.userName}>
				<span className={styles.userLabel}>Name: </span>{guest?.first_name} {guest?.last_name}
            </BaseTypography>
			<BaseTypography className={styles.field__value}>
				<span className={styles.userLabel}>Title: </span>{guest?.title}
			</BaseTypography>
			<BaseTypography className={styles.field__value}>
				<span className={styles.userLabel}>Company: </span>{guest?.company_name}
            </BaseTypography>
			{guest?.country.length > 0 && (
				<BaseTypography className={styles.field__value_country}>
					<span className={styles.userLabel}>Country: </span>{guest?.country.map((ct) =>	ct.name).join(', ')}
				</BaseTypography>
				)
			}
			{guest?.languages.length > 0 && (
				<BaseTypography className={styles.field__value_language}>
					<span className={styles.userLabel}>Languages: </span>{guest?.languages.map((language) => language.name).join(', ')}
				</BaseTypography>
				)
			}
			{guest?.gender && (
				<BaseTypography className={styles.field__value}>
					<span className={styles.userLabel}>Gender: </span>{guest?.gender}
				</BaseTypography>
			)}
          </div>
        </div>
		
		<div className={styles.expertises}>
			{guest?.expertise.map((el) => {
			  return (
				<div className={styles.expertises__item} key={el.id}>
				  <BaseChip> {el.name} </BaseChip>
				</div>
			  );
			})}
		</div>
      </div>
		<hr className={styles.hr} />
      
	  
      {guest?.videos.length > 0 && (
        <>
		  <h3>Video:</h3>
          <div className={styles.videoWrapper}>{videos}</div>
        </>
      )}
      <h3>LinkedIn:</h3>
      <Link target="_blank" href={guest?.linkedin}>{guest?.linkedin}</Link>
      {available && (
        <>
          <h3>Media suitable for:</h3>
          <div>{available}</div>
        </>
      )}
      {companyLogo && (
        <>
          <h3>Company Logo</h3>
          <Avatar
            className={styles.avatar}
            alt="Remy Sharp"
            src={String(companyLogo)}
          />
        </>
      )}
      {companyDescription && (
        <>
          <h3>Company description:</h3>
          <div className={styles.companyDescriptionWrapper}>
            {companyDescription}
          </div>
        </>
      )}
      <BaseDivider />
    </div>
  );
};

export const ContactGuestModal: React.FC<PContactGuestModal> = (props) => {
  const { open, onClose, onSubmit, loading } = props;
  const defaultEmailTemplate = useSelector(getDefaultEmailTemplate);
  const { guest } = useSelector((state: RootState) => state.currentGuest);
  
  const formik = useFormik({
    validationSchema: yup.object().shape({
      email_template_id: yup.number().required(validationErrors.REQUIRED_FIELD),
      date_interview: validationRules.requiredDefaultStringRule,
	  newTempTitle: yup.mixed().when('is_a_new_temp', {
        is: true,
        then: yup.mixed().required(validationErrors.REQUIRED_FIELD),
      }),
    }),
    initialValues: {
      email_template_id: '',
      date_interview: '',
      comment: '',
	  is_a_new_temp: false,
	  newTempSec: false,
	  newTempTitle: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { email_template_id } = formik.values;
  const { setFieldValue } = formik;
  useEffect(() => {
    if (defaultEmailTemplate && !email_template_id) {
      setFieldValue('email_template_id', defaultEmailTemplate.id);
    }
  }, [defaultEmailTemplate, setFieldValue, email_template_id]);
  const emailTemplates = useSelector(getEmailTemplatesForSelect);
  const templateValue = emailTemplates.find(
    (item) => item.value === email_template_id,
  );

  useEffect(() => {
    if (templateValue) {
      formik.setFieldValue('comment', templateValue.content);
    }
  }, [templateValue]);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Invite Guest for interview"
      isFullWidthTitleDivider
      paperStyles={{ maxWidth: '830px', width: '100%' }}>
      <GuestVideoList guest={guest} />
      {/* <div style={{ marginBottom: '1rem' }}>
        <h3>Video:</h3>
        <List>
          {guest?.videos?.map((item) => {
            return (
              <ListItem>
                <Link href={item}>
                  {item.length > 40 ? `${item.slice(0, 40)}...` : item}
                </Link>
              </ListItem>
            );
          })}
          <Divider />
        </List>
        <h3>LinkedIn:</h3>
        <Link href={guest?.linkedin}>{guest?.linkedin}</Link>
        <Divider />
      </div> */}
      <form onSubmit={formik.handleSubmit}>
        <FormFieldsContainer>
          <FormikField
            label="Invite using this email template:"
            name="email_template_id"
            formik={formik}
            inputComponent={EmailTemplatesSelect}
            onChange={(newValue: number) => {
              formik.setFieldValue('email_template_id', newValue);
            }}
          />

          <BaseDateTimePicker
            label="Invite for interview on:"
            value={formik.values.date_interview}
            errorText={formik.errors.date_interview}
            onChange={(value: any) => {
              formik.setFieldValue('date_interview', value);
            }}
            disablePast
          />
          <FormControl error={Boolean(formik.errors.comment)}>
            <BaseInputLabel>Question:</BaseInputLabel>
            <SunEditor
              setDefaultStyle="font-size: 16px; font-family: IBM Plex Sans;"
              showToolbar
              name="comment"
              setContents={
                templateValue ? templateValue.content : formik.values.comment
              }
              onBlur={(e, editorContent) => {
                formik.setFieldValue('comment', editorContent);
			    formik.setFieldValue('newTempSec', true);
              }}
              setOptions={{
                height: 200,
                fontSize: [36],
                buttonList: [
                  ...buttonList.formatting,
                  ['font', 'align', 'lineHeight'],
                ],
                // Other option
              }}
            />
            <BaseHelperText>{formik.errors.comment}</BaseHelperText>
          </FormControl>
		  {formik.values.newTempSec && (
				<>
				  <BaseButtonGroup
					value={formik.values.is_a_new_temp}
					label="Do you want to save the changes as a new template?"
					onChange={(value) => {
					  formik.setFieldValue('is_a_new_temp', value);
					}}
				  />
				  {formik.values.is_a_new_temp && (
					<>
					  <div>
						<BaseTextField
						  fullWidth
						  value={formik.values.newTempTitle}
						  errorText={formik.errors.newTempTitle}
						  onChange={formik.handleChange}
						  name="newTempTitle"
						  label="Enter the name of the new template"
						/>
					  </div>
					</>
				  )}
				</>
		  )}
		  
          <BaseButton type="submit" fullWidth loading={loading}>
            Invite the Guest
          </BaseButton>
        </FormFieldsContainer>
      </form>
    </BaseDialog>
  );
};
